"use client";

/**
 * Orik's integrations, as a page of the harness's Settings panel.
 *
 * Each card is one account Orik can act through: its status, what connecting it allows, and how to connect or
 * disconnect it. The OAuth providers link through Better Auth and return to `/settings`, which reopens this page
 * (`src/lib/agent/shell-links.ts`), and Tailscale authorizes in a window of its own. The
 * controls are the harness's own (`ui-primitives`), so the page reads like the Settings pages beside it.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ChevronDownIcon, NetworkIcon, ShieldCheckIcon } from "lucide-react";
import { harnessPrimitives } from "../../harness/primitives";
import { authClient } from "@/lib/auth-client";
import { pendingIntegrationError } from "@/lib/agent/shell-links";

/** One OAuth provider's facts, as `/api/settings` reports them. */
interface ProviderState {
  readonly configured: boolean;
  readonly accountId?: string;
  readonly oauthError: boolean;
  readonly needsReconnect?: boolean;
  readonly subdomain?: string;
}

/** What `/api/settings` reports. */
export interface SettingsData {
  readonly user: string;
  readonly google: ProviderState;
  readonly vercel: ProviderState;
  readonly github: ProviderState;
  readonly salesforce: ProviderState;
  readonly zendesk: ProviderState;
  readonly linear: ProviderState;
  readonly outreach: ProviderState;
}

type Tone = "success" | "warning" | "quiet";

/** The frame every integration shares: identity, status, what it does, its actions and its messages. */
function IntegrationCard({ mark, name, category, status, children, actions, footnote, error }: {
  mark: ReactNode; name: string; category: string; status: { label: string; tone: Tone };
  children: ReactNode; actions: ReactNode; footnote?: ReactNode; error?: string | undefined;
}): ReactNode {
  const { Tag } = harnessPrimitives();
  return <article aria-label={name} className="rounded-xl border border-border bg-card p-5">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="flex size-9 items-center justify-center rounded-lg border border-border bg-background text-sm font-semibold">{mark}</span>
        <div><h3 className="text-sm font-semibold">{name}</h3><p className="mt-0.5 text-xs text-muted-foreground">{category}</p></div>
      </div>
      <span role="status"><Tag tone={status.tone}>{status.label}</Tag></span>
    </div>
    <div className="mt-3 flex flex-col gap-3 text-[13px] leading-5 text-muted-foreground">{children}</div>
    <div className="mt-4 flex flex-wrap gap-2">{actions}</div>
    {footnote !== undefined && <p className="mt-3 text-xs text-muted-foreground">{footnote}</p>}
    {error && <p role="alert" className="mt-3 text-[13px] text-destructive">{error}</p>}
  </article>;
}

/** An account linked through Better Auth's OAuth flow. */
interface OAuthProvider {
  readonly provider: "google" | "github" | "vercel" | "salesforce" | "zendesk" | "linear" | "outreach";
  readonly name: string;
  readonly category: string;
  readonly mark: ReactNode;
  readonly connectLabel?: string;
  description(state: ProviderState): ReactNode;
}

function OAuthIntegration({ spec, state, refresh, children }: { spec: OAuthProvider; state: ProviderState; refresh(): void; children?: ReactNode }): ReactNode {
  const { Button } = harnessPrimitives();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(state.oauthError ? `${spec.name} could not be connected. Please try again.` : "");
  const connected = state.accountId !== undefined;
  const reconnect = connected && state.needsReconnect === true;
  const connect = async (): Promise<void> => {
    setPending(true); setError("");
    try {
      const result = await authClient.linkSocial({ provider: spec.provider, callbackURL: "/settings", errorCallbackURL: `/settings?error=${spec.provider}` });
      if (result.error) throw new Error(result.error.message ?? `${spec.name} could not be connected.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : `${spec.name} could not be connected.`); setPending(false); }
  };
  const disconnect = async (): Promise<void> => {
    if (state.accountId === undefined) return;
    setPending(true); setError("");
    try {
      const result = await authClient.unlinkAccount({ accountId: state.accountId });
      if (result.error) throw new Error(result.error.message ?? `${spec.name} could not be disconnected.`);
      refresh();
    } catch (reason) { setError(reason instanceof Error ? reason.message : `${spec.name} could not be disconnected.`); }
    finally { setPending(false); }
  };
  return <IntegrationCard mark={spec.mark} name={spec.name} category={spec.category} error={error}
    status={reconnect ? { label: "Reconnect required", tone: "warning" } : connected ? { label: "Connected", tone: "success" } : { label: "Not connected", tone: "quiet" }}
    actions={<>
      <Button variant="primary" disabled={!state.configured || pending} onClick={() => { void connect(); }}>{pending ? "Working…" : connected ? `Reconnect ${spec.name}` : spec.connectLabel ?? `Connect ${spec.name}`}</Button>
      {connected && <Button variant="outline" disabled={pending} onClick={() => { void disconnect(); }}>Disconnect</Button>}
    </>}
    footnote={connected ? `Disconnecting removes Orik’s stored connection. You can also revoke access in your ${spec.name} account permissions.` : undefined}>
    <p>{spec.description(state)}</p>
    {children}
    {reconnect && <p role="alert" className="rounded-lg bg-warning/10 p-3 text-warning">Reconnect {spec.name} to grant the access Orik now needs.</p>}
    {!state.configured && <p className="text-foreground">{spec.name} integration is not configured. Contact your Orik administrator.</p>}
  </IntegrationCard>;
}

const GOOGLE: OAuthProvider = {
  provider: "google", name: "Google", category: "Workspace & Marketing", mark: "G",
  description: () => "Analyze Google Analytics traffic and conversions and Search Console search performance, explore and manage Google Ads campaigns and keyword ideas, and search Gmail, Calendar, Contacts, and Drive files.",
};
const GITHUB: OAuthProvider = {
  provider: "github", name: "GitHub", category: "Repositories & code", mark: "GH",
  description: () => "Search repositories, work with code, and create pull requests using your linked GitHub account. Access is limited by your GitHub App installation and your own repository permissions.",
};
const VERCEL: OAuthProvider = {
  provider: "vercel", name: "Vercel", category: "Projects & deployments", mark: "▲",
  description: () => "Manage projects and deployments in the Vercel team where you install the Orik integration. Orik can use only the access that installation grants.",
};
const SALESFORCE: OAuthProvider = {
  provider: "salesforce", name: "Salesforce", category: "CRM and Pardot (Account Engagement)", mark: "S",
  description: ({ subdomain }) => <>Read and write CRM records and Pardot marketing data with full API access. {subdomain && <>Connect your account for {subdomain}. </>}Requests run with your Salesforce permissions.</>,
};
const ZENDESK: OAuthProvider = {
  provider: "zendesk", name: "Zendesk", category: "Customer support", mark: "Z",
  description: ({ subdomain }) => <>Search, update, and reply to support tickets, and manage customers and organizations. {subdomain && <>Connect your account for {subdomain}.zendesk.com. </>}Orik requests read and write access.</>,
};

const LINEAR: OAuthProvider = {
  provider: "linear", name: "Linear", category: "Issues & projects", mark: "L",
  description: () => "Search, create, and update issues, projects, cycles, and comments in your Linear workspace. Orik acts as you, with your Linear permissions.",
};

const OUTREACH: OAuthProvider = {
  provider: "outreach", name: "Outreach", category: "Sales engagement", mark: "O",
  description: () => "Search and update prospects, accounts, opportunities, sequences, tasks, and calls in Outreach. Orik acts as you, with your Outreach permissions.",
};

/** Google's note on which account to choose and what its Ads consent covers. */
function GoogleAccess(): ReactNode {
  const [open, setOpen] = useState(false);
  return <details open={open} onToggle={event => setOpen(event.currentTarget.open)} className="rounded-lg border border-border text-xs">
    <summary className="flex list-none items-center gap-2 p-3 text-foreground [&::-webkit-details-marker]:hidden">
      <ShieldCheckIcon className="size-4 text-muted-foreground" aria-hidden />Permissions &amp; account access
      <ChevronDownIcon className={`ml-auto size-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
    </summary>
    <div className="flex flex-col gap-2 border-t border-border p-3">
      <p>Choose the Google account with access to your Analytics properties, Search Console properties and Ads accounts. Orik can do anything that account is allowed to do within the access you grant.</p>
      <p>Google Ads API access must be approved for the Google Cloud project used by Orik’s OAuth client. Google’s Ads consent grants account management access, so Orik can change campaigns in Ads accounts where your Google user has that permission.</p>
    </div>
  </details>;
}

type TailnetStatus = "loading" | "unavailable" | "disconnected" | "connected";

async function tailnetStatus(): Promise<TailnetStatus> {
  const response = await fetch("/api/credentials/tailscale", { cache: "no-store" });
  if (!response.ok) return "unavailable";
  const result = await response.json() as { provisioned?: boolean; needsReauth?: boolean };
  return result.provisioned && !result.needsReauth ? "connected" : "disconnected";
}

function TailscaleIntegration(): ReactNode {
  const { Button } = harnessPrimitives();
  const [status, setStatus] = useState<TailnetStatus>("loading");
  const [pending, setPending] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    void tailnetStatus().then(value => { if (!cancelled) setStatus(value); }, () => { if (!cancelled) setStatus("unavailable"); });
    return () => { cancelled = true; };
  }, []);
  // While the Tailscale window is open, check every two seconds for up to two minutes.
  useEffect(() => {
    if (!authorizing) return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async (): Promise<void> => {
      try {
        const value = await tailnetStatus();
        if (cancelled) return;
        if (value === "connected") { setStatus(value); setAuthorizing(false); return; }
      } catch { /* Retry transient status failures while authorization is open. */ }
      if (cancelled) return;
      if (++attempts >= 60) { setAuthorizing(false); setError("Tailscale connection was not completed. Please try again."); }
      else timer = setTimeout(() => { void poll(); }, 2_000);
    };
    timer = setTimeout(() => { void poll(); }, 2_000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [authorizing]);
  const toggle = async (): Promise<void> => {
    const disconnect = status === "connected";
    if (disconnect && !window.confirm("Disconnect the Orik agent from your Tailscale identity?")) return;
    setPending(true); setError("");
    try {
      const response = await fetch("/api/credentials/tailscale", { method: disconnect ? "DELETE" : "POST" });
      if (!response.ok) throw new Error(disconnect ? "Tailscale could not be disconnected. Please try again." : "Tailscale could not be connected. Please try again.");
      if (disconnect) { setStatus("disconnected"); return; }
      const result = await response.json() as { url?: string };
      if (!result.url) throw new Error("Tailscale could not be connected. Please try again.");
      window.open(result.url, "orik-tailscale", "popup,width=720,height=800,noopener,noreferrer");
      setAuthorizing(true);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Tailscale request failed. Please try again."); }
    finally { setPending(false); }
  };
  const label = status === "connected" ? "Connected" : status === "loading" ? "Checking connection…" : status === "unavailable" ? "Unavailable" : "Not connected";
  return <IntegrationCard mark={<NetworkIcon className="size-4" />} name="Tailscale" category="Private network access" error={error}
    status={{ label, tone: status === "connected" ? "success" : "quiet" }}
    actions={<Button variant={status === "connected" ? "outline" : "primary"} disabled={status === "loading" || pending || authorizing} onClick={() => { void toggle(); }}>
      {pending ? "Working…" : authorizing ? "Waiting for authorization…" : status === "connected" ? "Disconnect Tailscale" : "Connect Tailscale"}
    </Button>}>
    <p>Connect Tailscale so your Orik agent can access resources on your tailnet.</p>
    {status === "unavailable" && <p className="text-foreground">Tailscale status is unavailable. Try again later or contact your Orik administrator.</p>}
    {authorizing && <p role="status">Complete authorization in the Tailscale window. If it did not open, allow pop-ups and try again.</p>}
  </IntegrationCard>;
}

/** The deployment's account facts, read when the page opens and after a disconnect. */
export function useSettingsData(): { data?: SettingsData; failed: boolean; refresh(): void } {
  const [state, setState] = useState<{ data?: SettingsData; failed: boolean }>({ failed: false });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let live = true;
    const error = pendingIntegrationError();
    void (async () => {
      try {
        const response = await fetch(`/api/settings${error === undefined ? "" : `?error=${encodeURIComponent(error)}`}`, { headers: { accept: "application/json" } });
        if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
        const data = await response.json() as SettingsData;
        if (live) setState({ data, failed: false });
      } catch {
        if (live) setState({ failed: true });
      }
    })();
    return () => { live = false; };
  }, [revision]);
  const refresh = useCallback(() => { setRevision(value => value + 1); }, []);
  return { ...state, refresh };
}

/** The Integrations page. */
export function IntegrationsSection(): ReactNode {
  const { data, failed, refresh } = useSettingsData();
  return <section data-orik-ui="" aria-label="Integrations" className="flex flex-col gap-4 pb-2 font-sans text-foreground">
    <p className="text-[13px] leading-5 text-muted-foreground">Connect the accounts Orik works with on your behalf.</p>
    {failed && <p role="alert" className="text-[13px] text-destructive">Integrations could not be loaded. Close Settings and try again.</p>}
    {data === undefined && !failed && <p role="status" className="text-[13px] text-muted-foreground">Loading integrations…</p>}
    {data !== undefined && <>
      <OAuthIntegration spec={GOOGLE} state={data.google} refresh={refresh}><GoogleAccess /></OAuthIntegration>
      <TailscaleIntegration />
      <OAuthIntegration spec={VERCEL} state={data.vercel} refresh={refresh} />
      <OAuthIntegration spec={GITHUB} state={data.github} refresh={refresh} />
      <OAuthIntegration spec={LINEAR} state={data.linear} refresh={refresh} />
      <OAuthIntegration spec={SALESFORCE} state={data.salesforce} refresh={refresh} />
      <OAuthIntegration spec={OUTREACH} state={data.outreach} refresh={refresh} />
      <OAuthIntegration spec={ZENDESK} state={data.zendesk} refresh={refresh} />
    </>}
  </section>;
}
